import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { createApiProvider } from '../config/apiConfig';
import { getSelfOrderSettings, getMenuItemsAsync, getPublicTableDetails } from '../services/api/restApiAdapter';
import { LOCAL_STORAGE_RESTAURANT_ID_KEY } from '../constants';
import MoneyUtils from '../utils/moneyUtils';

const RestaurantContext = createContext();

export const useRestaurantContext = () => useContext(RestaurantContext);

const api = createApiProvider();

export const RestaurantProvider = ({ children }) => {
  const [restaurantId, setRestaurantId] = useState(null);
  const [restaurantData, setRestaurantData] = useState({ details: null, menuItems: [], reviews: [], theme: null });
  const [selfOrderConfig, setSelfOrderConfig] = useState({
    enabledModes: ['order'],
    requireSignIn: false,
    allowPayAndOrderWithoutSignIn: true,
    layoutType: 'list',
    gridColumns: 2,
    queueManagement: {
      enabled: false,
      allowPreOrder: true,
      maxEntriesPerUser: 50,
      mode: 'daywise',
      timingWindow: { start: '09:00', end: '22:00' },
      autoEnableHours: 2,
      autoStopMinutes: 30
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tableDetails, setTableDetails] = useState(null);
  const [tableNumber, setTableNumber] = useState(null);
  const [isTrackedTable, setIsTrackedTable] = useState(true);
  const [tableSection, setTableSection] = useState(null);
  const [initialOrderType, setInitialOrderType] = useState(null);
  const [urlModeOverride, setUrlModeOverride] = useState(null);

  // Dynamic Favicon and Title update
  useEffect(() => {
    const defaultTitle = 'Swadho - Self Order';
    const defaultFavicon = '/favicon.ico';

    if (restaurantData.details) {
      document.title = restaurantData.details.name || defaultTitle;
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.href = restaurantData.details.logoUrl || defaultFavicon;
      }
    } else {
      document.title = defaultTitle;
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) favicon.href = defaultFavicon;
    }
  }, [restaurantData.details]);

  const loadRestaurant = useCallback(async (id, optionalTableId = null) => {
    if (!id) {
      localStorage.removeItem(LOCAL_STORAGE_RESTAURANT_ID_KEY);
      setRestaurantId(null);
      setRestaurantData({ details: null, menuItems: [], reviews: [], theme: null });
      setTableDetails(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setRestaurantId(id);
    localStorage.setItem(LOCAL_STORAGE_RESTAURANT_ID_KEY, id);

    try {
      const tableIdToFetch = optionalTableId || sessionStorage.getItem(`swado_table_id_${id}`) || sessionStorage.getItem(`swado_table_${id}`);

      // ── Single parallel fetch — everything loads at the same time ────────────
      const [detailsRes, kioskConfigRes, menuItemsRes, tableRes] = await Promise.all([
        api.getPublicRestaurant(id),
        getSelfOrderSettings(id),
        getMenuItemsAsync(id),
        tableIdToFetch ? getPublicTableDetails(id, tableIdToFetch) : Promise.resolve(null),
      ]);

      if (!detailsRes.success) {
        throw new Error(detailsRes.error || 'Could not fetch restaurant details.');
      }

      // Configure MoneyUtils with this restaurant's currency settings
      if (detailsRes.data?.currencyConfig) {
        MoneyUtils.setCurrency(detailsRes.data.currencyConfig);
      }

      // If table details resolved from server
      if (tableRes?.success && tableRes.data) {
        const tbl = tableRes.data;
        setTableDetails(tbl);
        const nameOrNum = tbl.name?.trim() ? tbl.name.trim() : (tbl.tableNumber ? String(tbl.tableNumber) : null);
        setTableNumber(nameOrNum);
        setIsTrackedTable(!tbl.isUntracked);
        const fullLoc = [tbl.tableLocation, tbl.section].filter(Boolean).join(' • ');
        setTableSection(fullLoc || null);

        sessionStorage.setItem(`swado_table_id_${id}`, tbl.id);
        if (nameOrNum) sessionStorage.setItem(`swado_table_${id}`, nameOrNum);
        sessionStorage.setItem(`swado_tracked_${id}`, String(!tbl.isUntracked));
        if (fullLoc) sessionStorage.setItem(`swado_section_${id}`, fullLoc);
      }

      // Hydrate selfOrderConfig (falls back to defaults if missing)
      if (kioskConfigRes.success && kioskConfigRes.data) {
        const kc = kioskConfigRes.data;
        // Backward compat: server might return old `mode` string
        const enabledModes = Array.isArray(kc.enabledModes)
          ? kc.enabledModes
          : kc.mode ? [kc.mode] : ['order'];
        setSelfOrderConfig({
          enabledModes,
          requireSignIn: kc.requireSignIn || false,
          allowPayAndOrderWithoutSignIn: kc.allowPayAndOrderWithoutSignIn ?? true,
          layoutType: kc.layoutType || 'list',
          gridColumns: kc.gridColumns || 2,
          queueManagement: {
            enabled: kc.queueManagement?.enabled ?? false,
            allowPreOrder: kc.queueManagement?.allowPreOrder ?? true,
            maxEntriesPerUser: kc.queueManagement?.maxEntriesPerUser || 2,
            mode: kc.queueManagement?.mode || 'daywise',
            timingWindow: {
               start: kc.queueManagement?.timingWindow?.start || '00:00',
               end: kc.queueManagement?.timingWindow?.end || '23:59'
            },
            autoEnableHours: kc.queueManagement?.autoEnableHours ?? 2,
            autoStopMinutes: kc.queueManagement?.autoStopMinutes ?? 30
          }
        });
      }

      // Derive theme from brandColors on the restaurant model
      // brandColors: { primary (brand accent), text (text on accent), background }
      const bc = detailsRes.data?.brandColors;
      const derivedTheme = bc
        ? { secondary: bc.primary, primary: bc.text, background: bc.background }
        : null;

      // Single atomic state update — UI paints once with all data ready
      setRestaurantData({
        details: detailsRes.data,
        menuItems: menuItemsRes.success && Array.isArray(menuItemsRes.data) ? menuItemsRes.data : [],
        reviews: [],
        theme: derivedTheme,
      });

    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Compute Cascading Permissions & Capabilities Hierarchy
  const capabilities = React.useMemo(() => {
    const enabled = Array.isArray(selfOrderConfig?.enabledModes) ? selfOrderConfig.enabledModes : ['order'];
    
    // Base capabilities derived from enabledModes
    let canPayAndOrder = enabled.includes('payAndOrder');
    let canOrder = enabled.includes('order') || canPayAndOrder;
    let canShowCode = enabled.includes('showCode') || canOrder || canPayAndOrder;

    if (enabled.length === 0) {
      canPayAndOrder = false;
      canOrder = false;
      canShowCode = false;
    }

    // Apply URL mode override if specified (e.g. ?mode=showCode)
    if (urlModeOverride === 'showCode') {
      canShowCode = true;
      canOrder = false;
      canPayAndOrder = false;
    } else if (urlModeOverride === 'order') {
      canShowCode = true;
      canOrder = true;
      canPayAndOrder = false;
    } else if (urlModeOverride === 'payAndOrder') {
      canShowCode = true;
      canOrder = true;
      canPayAndOrder = true;
    } else if (urlModeOverride === 'viewOnly') {
      canShowCode = false;
      canOrder = false;
      canPayAndOrder = false;
    }

    const isViewOnly = !canShowCode && !canOrder && !canPayAndOrder;
    const isOrderingEnabled = !isViewOnly;

    return {
      canPayAndOrder,
      canOrder,
      canShowCode,
      isViewOnly,
      isOrderingEnabled,
    };
  }, [selfOrderConfig?.enabledModes, urlModeOverride]);

  const value = {
    restaurantId,
    details: restaurantData.details,
    menuItems: restaurantData.menuItems,
    reviews: restaurantData.reviews,
    theme: restaurantData.theme,
    selfOrderConfig,
    isLoading,
    error,
    loadRestaurant,
    setRestaurantId,
    tableDetails,
    setTableDetails,
    tableNumber,
    setTableNumber,
    isTrackedTable,
    setIsTrackedTable,
    tableSection,
    setTableSection,
    initialOrderType,
    setInitialOrderType,
    urlModeOverride,
    setUrlModeOverride,
    capabilities,
  };

  return (
    <RestaurantContext.Provider value={value}>
      {children}
    </RestaurantContext.Provider>
  );
};
